import { useState } from 'react';
import { readDecimal } from '@workbook/shared';
import { Button } from './Button';
import { Dialog } from './Dialog';
import type { DensityLevel } from './layout/Density';
import { Field } from './Field';
import { Input } from './Input';
import { Keypad } from './Keypad';
import { problemWith } from './NumberInput';
import { Textarea } from './Textarea';
import type { PromptKind, PromptOptions } from './dialog-store';

/**
 * 🔴 A QUESTION THAT NEEDS AN ANSWER TYPED — design-system plan DS-8 (5.1; F497, F198, F210, F39). What
 * `window.prompt` was, drawn by the kit: a title (verb + object), the consequence, ONE labelled field, and the verb
 * on the confirm button.
 *
 * ⚠️ THE ANSWER IS READ BEFORE IT IS RETURNED, WHICH `window.prompt` NEVER DID. The till's reprice prompt put `NaN`
 * into the basket (F497) and a Bengali-digit price crashed a sale (F198): the text went straight to the screen. Here
 * the confirm button reads it first — an `amount` or `quantity` as a decimal, then the screen's own `validate` — and
 * while it is wrong the dialog stays open with the kit's field error under the box (`role="alert"`), and nothing
 * resolves. Once shown, the error follows the typing and goes the moment the text is right.
 *
 * `amount` reads what a cashier writes: Bengali digits, lakh (1,05,000) or western (105,000) commas, a leading ৳, Tk
 * or BDT and a trailing "/-" — exactly what the till's `parseTaka` accepts (dialog.spec.tsx holds the two to the same
 * answers), with the shared `readDecimal` underneath, so the portal and the console can borrow this without the till.
 * The promise resolves the CANONICAL decimal ("105000.5"), never a number. `keypad` puts the till's `Keypad` under an
 * amount or a quantity, its Enter key the confirm — and sets the box's `inputmode` to "none", so the focus the dialog
 * gives it does not raise a touch counter's soft keyboard over the pad (on a phone the dialog is a bottom sheet, and
 * the keyboard opens exactly where the pad is). The box keeps focus and its caret: a hardware keyboard or a wedge
 * scanner still types into it.
 */
const AFFIXES = { lead: /^(৳|tk\.?|bdt)\s*/i, trail: /\s*\/-$/ };

/** Money as a person writes it, as the canonical decimal string — or null. Never negative, at most four places. */
export function readAmount(text: string): string | null {
  return readDecimal(bareAmount(text), { decimals: 4 });
}

/** Without its currency: `readDecimal` maps the Bengali digits and reads the commas. */
function bareAmount(text: string): string {
  return text.trim().replace(AFFIXES.lead, '').replace(AFFIXES.trail, '');
}

/** "Price" → "price"; "IMEI" stays "IMEI". */
function lowerFirst(label: string): string {
  return /^[A-Z][a-z]/.test(label) ? `${label.charAt(0).toLowerCase()}${label.slice(1)}` : label;
}

export type Answer = { value: string; problem: null } | { value: null; problem: string };

/** The field's text read for its kind: the value to resolve, or the sentence that says what is wrong with it. */
export function readAnswer(text: string, { kind = 'text', required = true, label }: { kind?: PromptKind; required?: boolean; label: string }): Answer {
  const trimmed = text.trim();
  if (trimmed === '') return required ? { value: null, problem: `Enter the ${lowerFirst(label)}.` } : { value: '', problem: null };
  if (kind === 'amount' || kind === 'quantity') {
    const bare = kind === 'amount' ? bareAmount(trimmed) : trimmed;
    const problem = problemWith(bare, { kind, decimals: 4 });
    const value = readDecimal(bare, { decimals: 4 });
    if (problem !== null || value === null) return { value: null, problem: problem ?? 'Enter a number, such as 1,250.50.' };
    return { value, problem: null };
  }
  return { value: trimmed, problem: null };
}

/** `density` is the level of the subtree that asked (DS-9a): the host passes it with the question. */
export type PromptDialogProps = PromptOptions & { density?: DensityLevel; onConfirm: (value: string) => void; onCancel: () => void };

export function PromptDialog({
  title,
  body,
  label,
  kind = 'text',
  initial = '',
  hint,
  required = true,
  validate,
  confirmLabel,
  cancelLabel = 'Cancel',
  tone = 'default',
  keypad = false,
  density,
  onConfirm,
  onCancel,
}: PromptDialogProps) {
  const [text, setText] = useState(initial);
  // The problem last shown: set by the confirm button, then kept up to date as the person types.
  const [problem, setProblem] = useState<string | null>(null);
  const numeric = kind === 'amount' || kind === 'quantity';

  const check = (t: string): Answer => {
    const read = readAnswer(t, { kind, required, label });
    if (read.problem !== null) return read;
    const own = validate?.(read.value);
    return own ? { value: null, problem: own } : read;
  };
  const change = (t: string) => {
    setText(t);
    if (problem !== null) setProblem(check(t).problem);
  };
  const submit = () => {
    const read = check(text);
    if (read.problem !== null) {
      setProblem(read.problem);
      return;
    }
    onConfirm(read.value);
  };

  return (
    <Dialog
      title={title}
      body={body}
      density={density}
      onCancel={onCancel}
      onSubmit={submit}
      actions={
        <>
          <Button onClick={onCancel}>{cancelLabel}</Button>
          <Button
            type="submit"
            variant={tone === 'danger' ? 'danger' : 'primary'}
            className={tone === 'danger' ? 'wb-dialog__confirm--danger' : undefined}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <Field label={label} hint={hint} error={problem ?? undefined} required={required}>
        {kind === 'reason' ? (
          <Textarea value={text} onChange={(e) => change(e.target.value)} />
        ) : (
          <Input
            kind={numeric ? kind : 'text'}
            value={text}
            autoComplete="off"
            // The keypad IS the keyboard: no soft keyboard over it. Input spreads its props after its own
            // `decimal`, so the key is passed only with the keypad: an `inputMode={undefined}` would erase it.
            {...(keypad && numeric ? { inputMode: 'none' as const } : {})}
            onChange={(e) => change(e.target.value)}
          />
        )}
      </Field>
      {keypad && numeric ? <Keypad value={text} onChange={change} onEnter={submit} /> : null}
    </Dialog>
  );
}
